import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { describeErrorCause, logClientEvent } from "@/lib/client-events";

// Structural PII guard. React Query keys in this app are shaped
// `[entityType, operation, ...dynamicParams]` (e.g. useContactSearch's
// `["contacts", "search", organizationId, trimmed, limit, searchPhone]` where
// `trimmed` is the raw user-typed search term — a name, email, whatever). Only
// the leading `[entityType, operation]` prefix identifies WHAT failed; the
// trailing elements are parameter VALUES that can carry PII and are never
// needed in a (public-ish) GitHub issue title. Joining just the first 2
// segments makes it structurally impossible for any current or future hook to
// leak a dynamic parameter through client_events.name into an incident title —
// no downstream regex scrub can reliably tell a person's name from a stable id,
// so we cut it at the source. The SQL-side incident_normalize(name) scrub
// stays as defense-in-depth. `"contacts:search"` is also MORE faithful to the
// documented `name` contract ("Stable identifier — query/mutation key or an
// action name", lib/client-events.ts) than the old full parameter dump was.
const keyToName = (key: unknown): string =>
  Array.isArray(key) ? key.slice(0, 2).map((k) => String(k)).join(":") : String(key ?? "unknown");

// The wrapped failure behind an Error thrown with a `cause` (the PostgREST or
// network error behind a fixed user-facing message) goes under `detail.cause`
// only, never into `message` or `name`: the incident pipeline derives its
// signature from those two. The key is absent when there is no cause, so an
// event for a cause-less error is exactly what it was before.
const withCause = (error: unknown): { cause: ReturnType<typeof describeErrorCause> } | undefined => {
  const cause = describeErrorCause(error);
  return cause ? { cause } : undefined;
};

/**
 * The one shared QueryClient. `staleTime: 5min` matches Forge's default —
 * slow-changing identity / org / settings queries stay cached across route
 * changes instead of refetching on every mount.
 *
 * Global error telemetry: every failed query/mutation is funnelled to
 * relay.client_events so frontend regressions are self-reporting instead
 * of relying on a user noticing and reporting "this doesn't work".
 *
 * Built here rather than inline in main.tsx so a test can construct it:
 * main.tsx renders the app at import time.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const cause = withCause(error);
        logClientEvent({
          level: "error",
          source: "query",
          name: keyToName(query.queryKey),
          message: error instanceof Error ? error.message : String(error),
          detail: { queryHash: query.queryHash, ...cause },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        const cause = withCause(error);
        logClientEvent({
          level: "error",
          source: "mutation",
          name: keyToName(mutation.options.mutationKey ?? "mutation"),
          message: error instanceof Error ? error.message : String(error),
          // No `detail` at all without a cause: logClientEvent's `{}` default
          // keeps these rows identical to what they were.
          ...(cause ? { detail: cause } : {}),
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
      },
    },
  });
}
